import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import Field from "../components/Field";
import FormMessage from "../components/FormMessage";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const STATUS_NOTES: Record<string, string> = {
  PENDING: "Your application is still under review. We'll email you when there's news.",
  APPROVED_UNPAID:
    "You're approved — finish setting up your membership using the invite link we emailed you.",
  LAPSED: "Your membership lapsed. Resubscribe from your portal to restore access.",
  REJECTED: "Your account isn't active. If you think this is a mistake, get in touch.",
};

export default function Login() {
  const { login, user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get("next") || "/portal";
  const statusParam = params.get("status");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If already signed in, jump straight to the portal (or wherever).
  useEffect(() => {
    if (!loading && user) navigate(next, { replace: true });
  }, [loading, user, next, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Members"
      title="welcome back."
      intro="Sign in to your book club portal."
      footer={
        <>
          Not a member yet? <Link to="/apply" style={{ textDecoration: "underline" }}>Apply now</Link>
        </>
      }
    >
      {statusParam && STATUS_NOTES[statusParam] && (
        <FormMessage tone="info">{STATUS_NOTES[statusParam]}</FormMessage>
      )}
      {error && <FormMessage tone="error">{error}</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <button
          type="submit"
          className="btn btn--primary"
          disabled={submitting}
          style={{ width: "100%", marginTop: "0.5rem" }}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
        <Link
          to="/forgot-password"
          className="eyebrow"
          style={{ color: "var(--c-palm)" }}
        >
          Forgot password?
        </Link>
      </div>
    </AuthShell>
  );
}
